import { useEffect, useRef, useState, useCallback } from 'react'
import { logger } from '@magnetar/utils'

export type WebSocketStatus = 'connecting' | 'connected' | 'disconnected' | 'error' | 'reconnecting'

export interface WebSocketOptions {
  /** 唯一識別這個連線的 key（用於全局共享連線） */
  connectionKey: string
  /** WebSocket URL */
  url: string
  /** 是否啟用連線（預設 true） */
  enabled?: boolean
  /** 最大重連次數（預設 5） */
  maxReconnectAttempts?: number
  /** 收到訊息時的回調 */
  onMessage?: (data: unknown) => void
}

interface WebSocketManager {
  socket: WebSocket | null
  subscribers: Set<(status: WebSocketStatus, data: unknown) => void>
  status: WebSocketStatus
  lastData: unknown
  reconnectAttempts: number
  reconnectTimeout: NodeJS.Timeout | null
  isUnmounted: boolean
  url: string
}

// 全局連接管理器 - 同一個 connectionKey 共享同一個 WebSocket
const connectionManagers = new Map<string, WebSocketManager>()

const cleanupManager = (connectionKey: string) => {
  const manager = connectionManagers.get(connectionKey)
  if (manager) {
    if (manager.reconnectTimeout) {
      clearTimeout(manager.reconnectTimeout)
    }
    if (manager.socket) {
      manager.socket.close(1000, 'Manager cleanup')
    }
    connectionManagers.delete(connectionKey)
  }
}

const getOrCreateManager = (connectionKey: string, url: string): WebSocketManager => {
  const existing = connectionManagers.get(connectionKey)
  if (existing) {
    // 如果 URL 改變了，需要重新連接
    if (existing.url !== url) {
      cleanupManager(connectionKey)
    } else {
      return existing
    }
  }

  const manager: WebSocketManager = {
    socket: null,
    subscribers: new Set(),
    status: 'disconnected',
    lastData: null,
    reconnectAttempts: 0,
    reconnectTimeout: null,
    isUnmounted: false,
    url,
  }

  connectionManagers.set(connectionKey, manager)
  return manager
}

const notifySubscribers = (manager: WebSocketManager, data?: unknown) => {
  manager.subscribers.forEach((callback) => callback(manager.status, data ?? manager.lastData))
}

const connectWebSocket = (
  connectionKey: string,
  manager: WebSocketManager,
  maxReconnectAttempts: number,
) => {
  if (manager.isUnmounted) return

  // 防止重複連接
  if (
    manager.socket &&
    (manager.socket.readyState === WebSocket.CONNECTING ||
      manager.socket.readyState === WebSocket.OPEN)
  ) {
    logger.log(`[useWebSocket] Already connecting/connected for ${connectionKey}, skipping`)
    return
  }

  // 清理舊連接
  if (manager.socket) {
    manager.socket.close(1000, 'Creating new connection')
    manager.socket = null
  }

  const isReconnecting = manager.reconnectAttempts > 0
  manager.status = isReconnecting ? 'reconnecting' : 'connecting'
  notifySubscribers(manager)

  logger.log(
    `[useWebSocket] Connecting to ${manager.url} (attempt ${manager.reconnectAttempts + 1}) for ${connectionKey}`,
  )

  try {
    const ws = new WebSocket(manager.url)
    manager.socket = ws

    // 連接超時
    const connectionTimeout = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        logger.warn(`[useWebSocket] Connection timeout for ${connectionKey}`)
        ws.close(1000, 'Connection timeout')
      }
    }, 10000)

    ws.onopen = () => {
      if (manager.isUnmounted) return
      clearTimeout(connectionTimeout)
      logger.log(`[useWebSocket] Connected successfully for ${connectionKey}`)
      manager.status = 'connected'
      manager.reconnectAttempts = 0
      notifySubscribers(manager)
    }

    ws.onmessage = (event) => {
      if (manager.isUnmounted) return

      try {
        const data = JSON.parse(event.data)
        manager.lastData = data
        notifySubscribers(manager, data)
      } catch (error) {
        logger.error(`[useWebSocket] Failed to parse message for ${connectionKey}:`, error)
      }
    }

    ws.onerror = (error) => {
      if (manager.isUnmounted) return
      logger.error(`[useWebSocket] Error for ${connectionKey}:`, error)
      manager.status = 'error'
      notifySubscribers(manager)
    }

    ws.onclose = (event) => {
      if (manager.isUnmounted) return

      logger.log(
        `[useWebSocket] Closed for ${connectionKey}. Code: ${event.code}, Reason: ${event.reason}`,
      )
      manager.socket = null

      if (event.code === 1000) {
        manager.status = 'disconnected'
        notifySubscribers(manager)
        return
      }

      manager.status = 'disconnected'
      notifySubscribers(manager)

      // 重連邏輯
      if (manager.reconnectAttempts < maxReconnectAttempts) {
        manager.reconnectAttempts++

        const baseDelay = 1000 * Math.pow(2, manager.reconnectAttempts - 1)
        const jitter = Math.random() * 1000
        const reconnectDelay = Math.min(baseDelay + jitter, 16000)

        logger.log(
          `[useWebSocket] Reconnecting in ${Math.round(reconnectDelay)}ms (attempt ${manager.reconnectAttempts}/${maxReconnectAttempts})`,
        )

        manager.reconnectTimeout = setTimeout(() => {
          if (!manager.isUnmounted) {
            connectWebSocket(connectionKey, manager, maxReconnectAttempts)
          }
        }, reconnectDelay)
      } else {
        logger.error(
          `[useWebSocket] Max reconnection attempts (${maxReconnectAttempts}) reached for ${connectionKey}`,
        )
        manager.status = 'error'
        notifySubscribers(manager)
      }
    }
  } catch (error) {
    logger.error(`[useWebSocket] Failed to create connection for ${connectionKey}:`, error)
    manager.status = 'error'
    notifySubscribers(manager)
  }
}

export function useWebSocket<T = unknown>(options: WebSocketOptions) {
  const { connectionKey, url, enabled = true, maxReconnectAttempts = 5, onMessage } = options

  const [status, setStatus] = useState<WebSocketStatus>('disconnected')
  const [data, setData] = useState<T | null>(null)
  const callbackRef = useRef<((status: WebSocketStatus, data: unknown) => void) | null>(null)
  const onMessageRef = useRef(onMessage)

  // 保持 onMessage 最新
  onMessageRef.current = onMessage

  // 創建訂閱回調
  const handleUpdate = useCallback((newStatus: WebSocketStatus, newData: unknown) => {
    setStatus(newStatus)
    if (newData) {
      setData(newData as T)
      onMessageRef.current?.(newData)
    }
  }, [])

  callbackRef.current = handleUpdate

  useEffect(() => {
    if (!enabled || !url) return

    logger.log(`[useWebSocket] Subscribing to ${connectionKey}`)

    const manager = getOrCreateManager(connectionKey, url)
    manager.subscribers.add(callbackRef.current!)

    // 如果已有數據，立即更新
    if (manager.lastData || manager.status !== 'disconnected') {
      setStatus(manager.status)
      if (manager.lastData) {
        setData(manager.lastData as T)
      }
    }

    // 如果沒有連接，創建連接
    if (!manager.socket || manager.socket.readyState === WebSocket.CLOSED) {
      connectWebSocket(connectionKey, manager, maxReconnectAttempts)
    }

    return () => {
      logger.log(`[useWebSocket] Unsubscribing from ${connectionKey}`)

      if (callbackRef.current) {
        manager.subscribers.delete(callbackRef.current)
      }

      // 如果沒有訂閱者了，清理連接
      if (manager.subscribers.size === 0) {
        logger.log(`[useWebSocket] No more subscribers for ${connectionKey}, cleaning up`)
        manager.isUnmounted = true
        cleanupManager(connectionKey)
      }
    }
  }, [connectionKey, url, enabled, maxReconnectAttempts])

  // 手動重連
  const reconnect = useCallback(() => {
    const manager = connectionManagers.get(connectionKey)
    if (manager) {
      manager.reconnectAttempts = 0
      manager.isUnmounted = false
      connectWebSocket(connectionKey, manager, maxReconnectAttempts)
    }
  }, [connectionKey, maxReconnectAttempts])

  return {
    status,
    data,
    isConnected: status === 'connected',
    isConnecting: status === 'connecting' || status === 'reconnecting',
    reconnect,
  }
}
